import { format } from 'date-fns';
import {
  ColumnMapping,
  convertExcelDate,
  ExcelProcessorConfig,
  HeaderConfig,
  ParsedExcelData,
  parseNumber,
  ValidationError
} from '@/lib/excel';
import { generateSoChungTuHienTai } from '@/lib/stringUtil';
import { roundNumber } from '@/lib/formatUtils';
import { MA_CHUNG_TU } from '@/constants';

type ExcelCellValue = string | number | Date | undefined;
type ExcelSection = Record<string, ExcelCellValue[]>;
type UUIDSectionResponse = Record<string, (string | null)[]>;
type VoucherInfo = {
  ngay_hl?: string;
  i_so_ct_ht?: string | number;
  chung_tu?: string;
  ma_ct?: string;
  so_ct_mau?: string;
};
type UUIDLookupResponse = {
  main?: UUIDSectionResponse;
  chi_tiet?: UUIDSectionResponse;
  chung_tu?: VoucherInfo | VoucherInfo[];
};

const cellToString = (value: ExcelCellValue) => (value == null ? '' : String(value));
const cellToNumber = (value: ExcelCellValue) => (value == null || value instanceof Date ? undefined : value);

const HEADERS = {
  // Main fields
  SO_CT: 'Số c/từ*',
  NGAY_CT: 'Ngày chứng từ*',
  MA_NT: 'Ngoại tệ*',
  TY_GIA: 'Tỷ giá',
  STATUS: 'Loại xuất*',
  SO_BUOC: 'Số bước',
  MA_GD: 'Giao dịch*',
  MA_KHO: 'Mã kho xuất*',
  DIEN_GIAI: 'Diễn giải',
  // Detail fields
  MA_VT: 'Mã sản phẩm*',
  TEN_VT: 'Tên sản phẩm',
  DVT: 'Đvt*',
  MA_KHON: 'Mã kho nhập*',
  MA_VI_TRI: 'Từ vị trí',
  MA_VI_TRIN: 'Đến vị trí',
  MA_LO: 'Mã lô',
  SO_LUONG: 'Số lượng*',
  PX_DD: 'Đích danh',
  GIA_NT: 'Giá',
  TIEN_NT: 'Tiền',
  TK_VT: 'Tk có',
  MA_NX_CT: 'Lý do đ/c',
  TK_DU: 'Tk nợ',
  MA_BP: 'Bộ phận'
} as const;

const HEADER_CONFIG: HeaderConfig[] = [
  // Main fields
  { header: HEADERS.SO_CT, key: 'so_ct', type: 'main' },
  { header: HEADERS.NGAY_CT, key: 'ngay_ct', type: 'main' },
  { header: HEADERS.MA_NT, key: 'ma_nt', type: 'main' },
  { header: HEADERS.TY_GIA, key: 'ty_gia', type: 'main' },
  { header: HEADERS.STATUS, key: 'status', type: 'main' },
  { header: HEADERS.SO_BUOC, key: 'so_buoc', type: 'main' },
  { header: HEADERS.MA_GD, key: 'ma_gd', type: 'main' },
  { header: HEADERS.MA_KHO, key: 'ma_kho', type: 'main' },
  { header: HEADERS.DIEN_GIAI, key: 'dien_giai', type: 'main' },
  // Detail fields
  { header: HEADERS.MA_VT, key: 'ma_vt', type: 'chi_tiet' },
  { header: HEADERS.TEN_VT, key: 'ten_vt', type: 'chi_tiet' },
  { header: HEADERS.DVT, key: 'dvt', type: 'chi_tiet' },
  { header: HEADERS.MA_KHON, key: 'ma_khon', type: 'chi_tiet' },
  { header: HEADERS.MA_VI_TRI, key: 'ma_vi_tri', type: 'chi_tiet' },
  { header: HEADERS.MA_VI_TRIN, key: 'ma_vi_trin', type: 'chi_tiet' },
  { header: HEADERS.MA_LO, key: 'ma_lo', type: 'chi_tiet' },
  { header: HEADERS.SO_LUONG, key: 'so_luong', type: 'chi_tiet' },
  { header: HEADERS.PX_DD, key: 'px_dd', type: 'chi_tiet' },
  { header: HEADERS.GIA_NT, key: 'gia_nt', type: 'chi_tiet' },
  { header: HEADERS.TIEN_NT, key: 'tien_nt', type: 'chi_tiet' },
  { header: HEADERS.TK_VT, key: 'tk_vt', type: 'chi_tiet' },
  { header: HEADERS.MA_NX_CT, key: 'ma_nx', type: 'chi_tiet' },
  { header: HEADERS.TK_DU, key: 'tk_du', type: 'chi_tiet' },
  { header: HEADERS.MA_BP, key: 'ma_bp', type: 'chi_tiet' }
];

const UUID_FIELDS = {
  main: ['ma_nt', 'ma_gd', 'ma_kho'],
  chi_tiet: ['ma_vt', 'dvt', 'ma_khon', 'ma_vi_tri', 'ma_vi_trin', 'ma_lo', 'tk_vt', 'ma_nx', 'tk_du', 'ma_bp']
};

const getFieldName = (key: string) => {
  const config = HEADER_CONFIG.find(c => c.key === key);
  return config?.header?.replace('*', '').trim() || key;
};

const hasData = (item: Record<string, ExcelCellValue>, keys: string[]) =>
  keys.some(k => item[k] != null && item[k] !== '' && item[k] !== 0);

const UUIDPayload = (data: ParsedExcelData) => {
  const build = (section: ExcelSection, fields: string[]) =>
    Object.fromEntries(fields.map(f => [f, section?.[f] || []]));

  const main = data.main || {};
  const chi_tiet = data.chi_tiet || {};

  const seenDates = new Set<string>();
  const chungTuArr: Array<{ ma_ct: string; ngay_hl: string }> = [];

  (main.so_ct || []).forEach((_, index: number) => {
    const ngay =
      convertExcelDate(main.ngay_lct?.[index]) ||
      convertExcelDate(main.ngay_ct?.[index]) ||
      format(new Date(), 'yyyy-MM-dd');

    const ngay_hl = ngay || format(new Date(), 'yyyy-MM-dd');
    if (seenDates.has(ngay_hl)) return;
    seenDates.add(ngay_hl);

    chungTuArr.push({
      ma_ct: MA_CHUNG_TU.TON_KHO.PHIEU_XUAT_DIEU_CHUYEN,
      ngay_hl
    });
  });

  return {
    main: {
      ...build(main, UUID_FIELDS.main)
    },
    chi_tiet: build(chi_tiet, UUID_FIELDS.chi_tiet),
    chung_tu: chungTuArr
  };
};

const ValidateUUID = (
  response: UUIDLookupResponse,
  columnMapping: Record<string, ColumnMapping>
): ValidationError[] => {
  if (!response) return [];
  const errors: ValidationError[] = [];

  (['main', 'chi_tiet'] as const).forEach(section => {
    const data = response[section];
    if (!data) return;

    Object.entries(data).forEach(([field, values]) => {
      if (!Array.isArray(values)) return;
      const mapping = Object.values(columnMapping).find(m => m.key === field && m.type === section);

      values.forEach((val, i) => {
        if (val === null) {
          errors.push({
            row: i + 2,
            field,
            message: `${getFieldName(field)} không hợp lệ`,
            section,
            columnIndex: mapping?.index
          });
        }
      });
    });
  });

  return errors;
};

const calculateTotals = (chiTiet: ImportBody['chi_tiet']) => {
  let t_so_luong = 0;
  let t_tien_nt = 0;
  let t_tien = 0;
  let t_gia = 0;

  chiTiet.forEach(item => {
    t_so_luong += item.so_luong || 0;
    t_tien_nt += item.tien_nt || 0;
    t_tien += item.tien_nt || 0;
    t_gia += item.gia || 0;
  });

  return {
    t_so_luong: roundNumber(t_so_luong, 6),
    t_tien_nt: roundNumber(t_tien_nt, 6),
    t_tien: roundNumber(t_tien, 6),
    t_gia: roundNumber(t_gia, 6)
  };
};

const processMainRows = (
  main: ExcelSection,
  UUIDResponse: UUIDLookupResponse,
  entityUnitUUID: string,
  rowCount: number
): Map<string, ImportBody> => {
  const groupedRecords = new Map<string, ImportBody>();
  const voucherCounters = new Map<string, number>();
  const voucherArray = Array.isArray(UUIDResponse?.chung_tu)
    ? UUIDResponse.chung_tu
    : UUIDResponse?.chung_tu
      ? [UUIDResponse.chung_tu]
      : [];

  const voucherByDate = new Map<string, VoucherInfo>();
  voucherArray.forEach(voucher => {
    const normalizedDate = voucher?.ngay_hl?.slice(0, 10);
    if (!normalizedDate) return;
    const existing = voucherByDate.get(normalizedDate);
    if (!existing || Number(voucher.i_so_ct_ht ?? 0) > Number(existing.i_so_ct_ht ?? 0)) {
      voucherByDate.set(normalizedDate, voucher);
    }
  });

  for (let i = 0; i < rowCount; i++) {
    const documentNumber = cellToString(main.so_ct?.[i]);
    const recordKey = `${documentNumber}`;

    if (!groupedRecords.has(recordKey)) {
      const transactionDate = convertExcelDate(main.ngay_lct?.[i]) || convertExcelDate(main.ngay_ct?.[i]);
      const documentDate = convertExcelDate(main.ngay_ct?.[i]) || transactionDate;
      const effectiveDate = transactionDate || documentDate || format(new Date(), 'yyyy-MM-dd');

      const voucherInfo = voucherByDate.get(effectiveDate) || voucherArray[i] || voucherArray[0];

      const counterKey = `${voucherInfo?.chung_tu || voucherInfo?.ma_ct || 'default'}_${effectiveDate}`;
      const base = voucherInfo?.i_so_ct_ht !== undefined ? Number(voucherInfo.i_so_ct_ht) : 0;
      const nextNumber = voucherCounters.has(counterKey) ? voucherCounters.get(counterKey)! : base + 1;
      voucherCounters.set(counterKey, nextNumber + 1);
      const nextVoucherNumber = voucherInfo ? nextNumber : undefined;
      const generatedVoucherNumber =
        voucherInfo?.so_ct_mau && nextVoucherNumber
          ? generateSoChungTuHienTai(nextVoucherNumber, voucherInfo.so_ct_mau, effectiveDate)
          : undefined;

      groupedRecords.set(recordKey, {
        so_ct: documentNumber || generatedVoucherNumber || undefined,
        ngay_ct: transactionDate || documentDate,
        ngay_lct: transactionDate || documentDate,
        i_so_ct: nextVoucherNumber || 0,
        ma_nt: UUIDResponse.main?.ma_nt?.[i] || cellToString(main.ma_nt?.[i]),
        ty_gia: Number(cellToNumber(main.ty_gia?.[i]) || 1),
        ma_nx: UUIDResponse.main?.ma_nx?.[i] || '',
        so_buoc: cellToString(main.so_buoc?.[i]) || '1',
        ma_gd: UUIDResponse.main?.ma_gd?.[i] || '',
        ma_kho: UUIDResponse.main?.ma_kho?.[i] || '',
        dien_giai: cellToString(main.dien_giai?.[i]).trim(),
        unit_id: entityUnitUUID,
        ma_ngv: '3',
        status: cellToString(main.status?.[i]),
        t_so_luong: 0,
        t_tien_nt: 0,
        t_tien: 0,
        ma_tthddt: '0',
        chi_tiet: []
      });
    }
  }

  return groupedRecords;
};

const processChiTietRows = (
  groupedRecords: Map<string, ImportBody>,
  main: ExcelSection,
  chi_tiet: ExcelSection,
  UUIDResponse: UUIDLookupResponse,
  rowCount: number
): Map<string, ImportBody['chi_tiet']> => {
  const chiTietMap = new Map<string, ImportBody['chi_tiet']>();
  const detailKeys = HEADER_CONFIG.filter(c => c.type === 'chi_tiet').map(c => c.key);

  for (let i = 0; i < rowCount; i++) {
    const documentNumber = cellToString(main.so_ct?.[i]);
    const recordKey = `${documentNumber}`;

    const record = groupedRecords.get(recordKey);
    if (!record) continue;

    const quantity = roundNumber(cellToNumber(chi_tiet.so_luong?.[i]), 6);
    const price = roundNumber(cellToNumber(chi_tiet.gia_nt?.[i]), 6);
    const amount = roundNumber(cellToNumber(chi_tiet.tien_nt?.[i]), 6) || roundNumber(quantity * price, 6);

    const detailsForKey = chiTietMap.get(recordKey) || [];

    const detailItem = {
      line: detailsForKey.length + 1,
      ma_vt: UUIDResponse.chi_tiet?.ma_vt?.[i] || '',
      dvt: UUIDResponse.chi_tiet?.dvt?.[i] || '',
      ma_kho: record.ma_kho || '',
      ma_khon: UUIDResponse.chi_tiet?.ma_khon?.[i] || '',
      ma_vi_tri: UUIDResponse.chi_tiet?.ma_vi_tri?.[i] || '',
      ma_vi_trin: UUIDResponse.chi_tiet?.ma_vi_trin?.[i] || '',
      ma_lo: UUIDResponse.chi_tiet?.ma_lo?.[i] || '',
      so_luong: quantity,
      px_dd: parseNumber(cellToNumber(chi_tiet.px_dd?.[i])),
      gia_nt: price,
      tien_nt: amount,
      gia: price,
      tien: amount,
      tk_vt: UUIDResponse.chi_tiet?.tk_vt?.[i] || '',
      ma_nx: UUIDResponse.chi_tiet?.ma_nx?.[i] || '',
      tk_du: UUIDResponse.chi_tiet?.tk_du?.[i] || '',
      ma_bp: UUIDResponse.chi_tiet?.ma_bp?.[i] || ''
    };

    if (hasData(detailItem, detailKeys)) {
      detailsForKey.push(detailItem);
      chiTietMap.set(recordKey, detailsForKey);
    }
  }

  return chiTietMap;
};

const TransformToBodies = (
  dataRows: ExcelCellValue[][],
  parsedData: ParsedExcelData,
  uuidResponse: UUIDLookupResponse,
  context: { entityUnitUUID: string; defaultMaGD?: string }
): ImportBody[] => {
  const main = parsedData.main || {};
  const chi_tiet = parsedData.chi_tiet || {};
  const rowCount = dataRows.length;

  const groupedRecords = processMainRows(main, uuidResponse, context.entityUnitUUID, rowCount);
  const chiTietMap = processChiTietRows(groupedRecords, main, chi_tiet, uuidResponse, rowCount);

  const records = Array.from(groupedRecords.values());
  records.forEach(record => {
    const key = `${record.so_ct || ''}`;
    record.chi_tiet = chiTietMap.get(key) || [];

    const totals = calculateTotals(record.chi_tiet);
    record.t_so_luong = totals.t_so_luong;
    record.t_tien_nt = totals.t_tien_nt;
    record.t_tien = totals.t_tien;
  });

  return records;
};

export interface ImportBody {
  so_ct?: string;
  ngay_ct?: string;
  ngay_lct?: string;
  ma_nt?: string;
  ty_gia?: number;
  ma_nx?: string;
  so_buoc?: string;
  ma_gd?: string;
  ma_kho?: string;
  dien_giai?: string;
  unit_id: string;
  ma_ngv: string;
  status?: string;
  i_so_ct?: number;
  t_so_luong?: number;
  t_tien_nt?: number;
  t_tien?: number;
  ma_tthddt?: string;
  chi_tiet: Array<{
    line: number;
    ma_vt: string;
    dvt: string;
    ma_kho: string;
    ma_khon?: string;
    ma_vi_tri?: string;
    ma_vi_trin?: string;
    ma_lo?: string;
    so_luong: number;
    px_dd?: number;
    gia_nt: number;
    tien_nt: number;
    gia: number;
    tien: number;
    tk_vt?: string;
    ma_nx?: string;
    tk_du?: string;
    ma_bp?: string;
  }>;
}

export const ExcelConfig: ExcelProcessorConfig<ImportBody> = {
  Headers: HEADERS,
  HeaderConfig: HEADER_CONFIG,
  Transform: TransformToBodies,
  UUIDPayload,
  ValidateUUID
};
